import React, { useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  startOfDay,
  addMonths,
  subMonths,
} from "date-fns";

const PRIMARY = "#1FADC6";
const LIGHT_RANGE = "#CFF2F6";
const RANGE_ANCHOR = "#0F7F90"; // darker than PRIMARY

export default function UnifiedCalendar({
  mode = "range",
  initialMonth,
  useInitialMonth = false,
  availabilityMap = {}, // ✅ NEW (OPTIONAL)
  allowedWeekDays,

  /* RANGE MODE (GLOBAL AVAILABILITY) */
  rangeStart,
  rangeEnd,
  onRangeSelect,
  onRangeSelectByDrag,
  disabled = false,

  /* SELECT MODE (BOOKING CALENDAR, ETC) */
  selectedDate,
  onSelectDate,
  selectableDates = [],
  disabledDates = [],
  preSelectedDates = [],

  /* OVERRIDE MODE */
  availabilityOverrides = [],
  availabilityRules = [],
  overrideDates = [], // <- SELECTED override dates (strings: "yyyy-MM-dd")
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (!useInitialMonth) {
      return startOfMonth(new Date());
    }

    const year = new Date().getFullYear();
    const monthIndex = Number(initialMonth);

    if (!Number.isNaN(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
      return new Date(year, monthIndex, 1);
    }

    return startOfMonth(new Date());
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  const handleTouchStart = (day) => {
    if (mode !== "range") {
      if (!isSelectable(day)) return;
      onSelectDate?.(format(day, "yyyy-MM-dd"));
      return;
    }

    if (!isSelectable(day)) return;

    setIsDragging(true);
    setDragStart(startOfDay(day));
    setDragEnd(startOfDay(day));
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element?.dataset?.date) return;

    setDragEnd(startOfDay(new Date(element.dataset.date)));
  };

  const handleTouchEnd = () => {
    if (!isDragging || !dragStart || !dragEnd) return;

    setIsDragging(false);

    const start = dragStart <= dragEnd ? dragStart : dragEnd;
    const end = dragStart <= dragEnd ? dragEnd : dragStart;

    onRangeSelectByDrag?.(start, end);

    setDragStart(null);
    setDragEnd(null);
  };

  /* ---------- OVERRIDE HELPERS ---------- */

  const normalizedOverrides = availabilityOverrides.map((o) => ({
    ...o,
    date: o.date.split("T")[0],
  }));

  const getOverrideForDay = (day) => {
    const d = format(day, "yyyy-MM-dd");
    return normalizedOverrides.find((o) => o.date === d);
  };

  const isFullDayClosed = (day) =>
    getOverrideForDay(day)?.type === "FullDayClosed";

  const isCustomWindow = (day) =>
    getOverrideForDay(day)?.type === "CustomWindow";

  const isBlockedRange = (day) =>
    getOverrideForDay(day)?.type === "BlockedRange";

  /* ---------- GLOBAL HELPERS ---------- */

  const stripTime = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  const isPast = (day) => {
    const todayTime = stripTime(new Date());
    const dayTime = stripTime(day);
    return dayTime < todayTime;
  };

  const parseLocal = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const isInGlobalAvailability = (day) => {
    if (!availabilityRules.length) return true;

    const time = startOfDay(day).getTime();
    return availabilityRules.some((r) => {
      const s = stripTime(parseLocal(r.startDate));
      const e = stripTime(parseLocal(r.endDate));
      return time >= s && time <= e;
    });
  };

  const isSelectable = (day) => {
    if (isPast(day)) return false;

    if (isFullDayClosed(day)) return false; // full day closed = not selectable

    if (mode === "range") {
      if (!allowedWeekDays?.length) return false;

      const dayName = format(day, "EEE"); // Mon, Tue...
      return allowedWeekDays.includes(dayName);
    }

    if (mode === "override") return isInGlobalAvailability(day);

    if (mode === "select") {
      const d = format(day, "yyyy-MM-dd");
      return selectableDates.includes(d) && !disabledDates.includes(d);
    }

    return false;
  };

  /* ---------- DRAG RANGE (GLOBAL TAB) ---------- */

  const handleMouseDown = (day) => {
    if (mode !== "range") return;
    if (!isSelectable(day)) return;

    setIsDragging(true);
    setDragStart(startOfDay(day));
    setDragEnd(startOfDay(day));
  };

  const handleMouseEnter = (day) => {
    if (!isDragging || !dragStart) return;
    if (!isSelectable(day)) return;

    setDragEnd(startOfDay(day));
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd) return;

    setIsDragging(false);

    const start = dragStart <= dragEnd ? dragStart : dragEnd;
    const end = dragStart <= dragEnd ? dragEnd : dragStart;

    onRangeSelectByDrag?.(start, end);

    setDragStart(null);
    setDragEnd(null);
  };

  /* ---------- CLICK ---------- */

  const handleClick = (day) => {
    if (disabled) return;

    if (mode !== "range") {
      if (!isSelectable(day)) return;
      onSelectDate?.(format(day, "yyyy-MM-dd"));
      return;
    }

    // range mode
    if (isDragging) return;
    onRangeSelect?.(startOfDay(day));
  };

  const prevMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const nextMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  /* ---------- BUILD GRID ---------- */

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  return (
    <div className="border rounded-xl p-4 bg-white">
      {/* HEADER */}
      <div className="flex justify-between mb-3">
        <button type="button" onClick={prevMonth}>
          ‹
        </button>
        <div className="font-semibold">{format(currentMonth, "MMMM yyyy")}</div>
        <button type="button" onClick={nextMonth}>
          ›
        </button>
      </div>

      {/* WEEK ROW */}
      <div className="grid grid-cols-7 text-xs font-semibold text-center mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* DAYS */}
      <div className="grid grid-cols-7 gap-0">
        {days.map((d, i) => {
          const dStr = format(d, "yyyy-MM-dd");
          const isSelectedOverride = selectedDate === dStr;

          const dTime = startOfDay(d).getTime();

          /* ===== RANGE TIMES ===== */
          let rangeStartTime = null;
          let rangeEndTime = null;

          if (rangeStart) {
            rangeStartTime = startOfDay(rangeStart).getTime();
          }

          if (rangeStart && rangeEnd) {
            const s = startOfDay(rangeStart).getTime();
            const e = startOfDay(rangeEnd).getTime();
            rangeStartTime = Math.min(s, e);
            rangeEndTime = Math.max(s, e);
          }

          if (mode === "range" && dragStart && dragEnd) {
            const s = startOfDay(dragStart).getTime();
            const e = startOfDay(dragEnd).getTime();
            rangeStartTime = Math.min(s, e);
            rangeEndTime = Math.max(s, e);
          }

          /* ===== FLAGS ===== */
          const isStart = rangeStart && rangeEnd && dTime === rangeStartTime;
          const isEnd = rangeStart && rangeEnd && dTime === rangeEndTime;

          const isInRange =
            rangeStart &&
            rangeEnd &&
            dTime > rangeStartTime &&
            dTime < rangeEndTime;

          const isSingleAnchor =
            rangeStart &&
            !rangeEnd &&
            dTime === startOfDay(rangeStart).getTime();

          const isRangeAnchor = isStart || isEnd;

          /* ===== OTHER FLAGS ===== */
          const disabled = !isSelectable(d);
          const fullClosed = isFullDayClosed(d);
          const custom = isCustomWindow(d);
          const blocked = isBlockedRange(d);

          const availability = availabilityMap?.[dStr];
          const availabilityStatus = availability?.available === true;
          const isOverride = availability?.overrideType;

          let style = {};

          /* ===== BASE STYLES ===== */
          if (isInRange && !isStart && !isEnd && !isSingleAnchor) {
            style = {
              background: LIGHT_RANGE,
              color: "#0F5E6A",
            };
          }

          /* ===== AVAILABILITY (ONLY NON-ANCHOR DAYS) ===== */
          if (availabilityStatus && !isSingleAnchor && !isStart && !isEnd) {
            style = {
              ...style,
              background: LIGHT_RANGE,
              border: "1px solid #1FADC6",
              color: "#0F5E6A",
              fontWeight: 500,
            };
          }
          if (mode === "select" && isSelectable(d) && selectedDate !== dStr) {
            style = {
              ...style,
              background: "#1FADC620", // light primary shade
              border: "1px solid #1FADC6",
              color: "#0F7F90",
              fontWeight: 500,
              borderRadius: "0.375rem",
            };
          }

          /* ===== OVERRIDES ===== */
          if (isOverride === "FULL_CLOSE") {
            style = {
              background: "#DC2626",
              color: "white",
              fontWeight: 600,
              borderRadius: "0.375rem",
            };
          } else if (isOverride === "CUSTOM_HOURS") {
            style = {
              background: "#F59E0B",
              color: "white",
              fontWeight: 600,
              borderRadius: "0.375rem",
            };
          } else if (isOverride === "CLOSURE_WINDOWS") {
            style = {
              background: "#7C3AED",
              color: "white",
              fontWeight: 600,
              borderRadius: "0.375rem",
            };
          }
          /* ===== SELECTED OVERRIDE HIGHLIGHT (Experience Flow Only) ===== */
          /* ===== SELECTED OVERRIDE HIGHLIGHT ===== */
          if (isSelectedOverride) {
            style = {
              background: RANGE_ANCHOR,
              color: "white",
              fontWeight: 700,
              borderRadius: "0.375rem",
            };
          }

          /* ===== 🔒 FINAL RANGE ANCHOR (LAST WORD) ===== */
          if ((isSingleAnchor || isStart || isEnd) && mode === "range") {
            style = {
              background: RANGE_ANCHOR,
              color: "white",
              fontWeight: 700,
              borderRadius: "0.375rem",
            };
          }

          return (
            <div
              key={i}
              data-date={dStr}
              onMouseDown={() => handleMouseDown(d)}
              onMouseEnter={() => handleMouseEnter(d)}
              onMouseUp={() => handleMouseUp(d)}
              onTouchStart={() => handleTouchStart(d)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={() => handleClick(d)}
              className={`h-9 flex items-center justify-center text-sm
          ${!isSameMonth(d, currentMonth) ? "opacity-30" : ""}
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
              style={style}
            >
              {format(d, "d")}
            </div>
          );
        })}
      </div>
    </div>
  );
}
